import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, FileText, Folder, Hash, Layers, ListTodo, MessageCircle } from '@/components/ui/icons';
import { useDb, useMe, type Tables } from '@/lib/store';
import { useUi, type RecentItem } from '@/lib/ui';
import { locationPath, useOpenTask, useWorkspaceId } from '@/lib/nav';
import { effectiveStatuses, locationName, statusOf } from '@/lib/hierarchy';
import { StatusIcon, statusProgress } from '@/components/fields/Status';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { Icon } from '@/components/ui/Icon';
import { HomeCard, CardEmpty } from './Card';

export interface RecentRow {
  key: string;
  icon: ReactNode;
  name: string;
  context?: string;
  go: () => void;
}

export function resolveRecent(db: Tables, r: RecentItem, ws: string, me: string, navigate: (p: string) => void, openTask: (id: string) => void): RecentRow | null {
  const key = `${r.kind}:${r.id}`;
  if (r.kind === 'location') {
    const loc = r.location ?? { type: 'list' as const, id: r.id };
    if (loc.type === 'workspace') {
      if (loc.id !== ws) return null;
      return { key, icon: <Layers size={15} />, name: 'Everything', context: db.workspaces[ws]?.name, go: () => navigate(locationPath(ws, loc)) };
    }
    if (loc.type === 'space') {
      const s = db.spaces[loc.id];
      if (!s || s.workspaceId !== ws || s.archived) return null;
      return { key, icon: <SpaceAvatar spaceId={s.id} size={16} />, name: s.name, go: () => navigate(locationPath(ws, loc)) };
    }
    if (loc.type === 'folder') {
      const f = db.folders[loc.id];
      const s = f ? db.spaces[f.spaceId] : undefined;
      if (!f || !s || s.workspaceId !== ws || f.archived) return null;
      return { key, icon: <Folder size={15} color={f.color ?? undefined} />, name: f.name, context: s.name, go: () => navigate(locationPath(ws, loc)) };
    }
    const l = db.lists[loc.id];
    const s = l ? db.spaces[l.spaceId] : undefined;
    if (!l || !s || s.workspaceId !== ws || l.archived) return null;
    const parent = l.folderId && db.folders[l.folderId] ? db.folders[l.folderId].name : s.name;
    return { key, icon: <ListTodo size={15} color={l.color ?? undefined} />, name: l.name, context: parent, go: () => navigate(locationPath(ws, loc)) };
  }
  if (r.kind === 'task') {
    const t = db.tasks[r.id];
    if (!t || t.workspaceId !== ws) return null;
    const status = statusOf(db, t);
    const parent = t.parentId ? db.tasks[t.parentId]?.name : undefined;
    return {
      key,
      icon: <StatusIcon type={status.type} color={status.color} size={14} progress={statusProgress(effectiveStatuses(db, t.listId), status)} />,
      name: t.name,
      context: parent ?? db.lists[t.listId]?.name,
      go: () => openTask(t.id),
    };
  }
  if (r.kind === 'doc') {
    const d = db.docs[r.id];
    if (!d || d.workspaceId !== ws || d.archived) return null;
    return {
      key,
      icon: !d.icon ? <FileText size={15} /> : /^[A-Za-z]/.test(d.icon) ? <Icon name={d.icon} size={15} /> : <span className="hm-emoji">{d.icon}</span>,
      name: d.title || 'Untitled',
      context: d.location ? locationName(db, d.location) : 'Docs',
      go: () => navigate(`/${ws}/docs/${d.id}`),
    };
  }
  const ch = db.channels[r.id];
  if (!ch || ch.workspaceId !== ws) return null;
  if (ch.kind === 'dm') {
    const other = ch.memberIds.find((u) => u !== me) ?? me;
    return { key, icon: <MessageCircle size={15} />, name: db.users[other]?.name ?? 'Direct message', context: 'Direct message', go: () => navigate(`/${ws}/chat/${ch.id}`) };
  }
  return { key, icon: <Hash size={15} />, name: ch.name, context: 'Chat', go: () => navigate(`/${ws}/chat/${ch.id}`) };
}

export function RecentsCard() {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const recents = useUi((s) => s.recents);
  const db = useDb((d) => d);
  const rows = useMemo(
    () => recents.map((r) => resolveRecent(db, r, ws, me, navigate, openTask)).filter((r): r is RecentRow => !!r),
    [recents, db, ws, me, navigate, openTask],
  );
  return (
    <HomeCard id="recents">
      {rows.length ? (
        <div className="hm-recents">
          {rows.map((r) => (
            <button key={r.key} className="hm-recent" onClick={r.go}>
              <span className="hm-recent-icon">{r.icon}</span>
              <span className="hm-recent-name truncate">{r.name}</span>
              {r.context && <span className="hm-recent-ctx truncate">• in {r.context}</span>}
            </button>
          ))}
        </div>
      ) : (
        <CardEmpty icon={<Clock size={22} />} text="Lists, tasks, docs and channels you open will show up here." />
      )}
    </HomeCard>
  );
}
